import FocusedViewFilter from 'owa-service/lib/contract/FocusedViewFilter';
import ViewFilter from 'owa-service/lib/contract/ViewFilter';
import { ErrorScreenText } from 'mini-error-handler/lib/schema/ErrorScreen.types';
import { format } from 'owa-strings';
import { getOwaMiniStrings } from 'owa-strings/owa-mini-strings';

const DEFAULT = 'Default';
const emptyScreenToImageMap: Partial<Record<string, string>> = {
    Inbox: 'resources/images/emptyInbox.svg',
    Archive: 'resources/images/emptyArchive.svg',
    'Deleted Items': 'resources/images/emptyDeletedItems.svg',
    Drafts: 'resources/images/emptyDrafts.svg',
    Flagged: 'resources/images/emptyFlaggedList.svg',
    'Junk Email': 'resources/images/emptyJunkEmail.svg',
    Default: 'resources/images/emptyListGeneric.svg',
    Mentioned: 'resources/images/emptyMentionedList.svg',
    Pinned: 'resources/images/emptyPinnedList.svg',
    HasFile: 'resources/images/emptyFileList.svg',
    'Sent Items': 'resources/images/emptySentItems.svg',
    Unread: 'resources/images/emptyUnreadList.svg',
};

const emptyScreenToDarkImageMap: Partial<Record<string, string>> = {
    Inbox: 'resources/images/emptyInboxDark.svg',
    Archive: 'resources/images/emptyArchiveDark.svg',
    'Deleted Items': 'resources/images/emptyDeletedItemsDark.svg',
    Drafts: 'resources/images/emptyDraftsDark.svg',
    Flagged: 'resources/images/emptyFlaggedListDark.svg',
    'Junk Email': 'resources/images/emptyJunkEmailDark.svg',
    Default: 'resources/images/emptyListGenericDark.svg',
    Mentioned: 'resources/images/emptyMentionedListDark.svg',
    Pinned: 'resources/images/emptyPinnedListDark.svg',
    HasFile: 'resources/images/emptyFileListDark.svg',
    'Sent Items': 'resources/images/emptySentItemsDark.svg',
    Unread: 'resources/images/emptyUnreadListDark.svg',
};

interface EmptyStateProperty {
    title: ErrorScreenText;
    subtitle: ErrorScreenText;
    imageSrc: string;
}
export const getEmptyStateDisplayProperties = (
    folderName?: string,
    viewFilter?: ViewFilter,
    focusedViewFilter?: FocusedViewFilter,
    isDarkTheme?: boolean
): EmptyStateProperty => {
    const viewFilterApplied: boolean = viewFilter !== 'All';

    if (viewFilterApplied) {
        return getEmptyFilterListStateProperties(viewFilter, isDarkTheme);
    } else {
        return getEmptyFolderListStateProperties(folderName, focusedViewFilter, isDarkTheme);
    }
};

const getEmptyFilterListStateProperties = (
    viewFilter: ViewFilter,
    isDarkTheme: boolean
): EmptyStateProperty => {
    const strings = getOwaMiniStrings();
    let titleText: string = null;
    let subtitleText: string = null;

    switch (viewFilter) {
        case 'Unread':
            titleText = strings.emptyUnreadFilterListTitle;
            subtitleText = strings.emptyFilterListSubtitle;
            break;
        case 'ToOrCcMe':
            titleText = strings.emptyToMeFilterListTitle;
            subtitleText = strings.emptyFilterListSubtitle;
            break;
        case 'Pinned':
            titleText = strings.emptyPinnedFilterListTitle;
            subtitleText = strings.emptyFilterListSubtitle;
            break;
        case 'HasFile':
            titleText = strings.emptyHasFilesFilterListTitle;
            subtitleText = strings.emptyHasFilesFilterListSubtitle;
            break;
        case 'Mentioned':
            titleText = strings.emptyMentionsMeFilterListTitle;
            subtitleText = strings.emptyFilterListSubtitle;
            break;
        case 'Flagged':
            titleText = strings.emptyFlaggedFilterListTitle;
            subtitleText = strings.emptyFilterListSubtitle;
            break;
    }
    const imageSrc = isDarkTheme
        ? emptyScreenToDarkImageMap[viewFilter] || emptyScreenToDarkImageMap[DEFAULT]
        : emptyScreenToImageMap[viewFilter] || emptyScreenToImageMap[DEFAULT];
    return {
        title: { text: titleText },
        subtitle: { text: subtitleText },
        imageSrc,
    };
};

const getEmptyFolderListStateProperties = (
    folderName: string,
    focusedViewFilter: FocusedViewFilter,
    isDarkTheme: boolean
): EmptyStateProperty => {
    const strings = getOwaMiniStrings();
    const seletedOtherInbox = focusedViewFilter === FocusedViewFilter.Other;
    let titleText = folderName
        ? format(strings.emptyMailListFolderTitle, folderName)
        : strings.emptyMailListDefaultFolderTitle;
    let subtitleText = strings.emptyCustomFolderSubtitle;

    switch (folderName) {
        case 'Inbox':
            (titleText = seletedOtherInbox
                ? strings.emptyOtherInboxFolderTitle
                : strings.emptyFocusedInboxFolderTitle),
                (subtitleText = strings.emptyInboxFolderSubtitle);
            break;

        case 'Junk Email':
            (titleText = strings.emptyJunkFolderTitle),
                (subtitleText = strings.emptyJunkAndDeletedFolderSubtitle);
            break;
        case 'Archive':
            // titletext is default value "Nothing in Archive"
            subtitleText = strings.emptyArchiveFolderSubtitle;
            break;
        case 'Sent Items':
            titleText = strings.emptySentFolderTitle;
            subtitleText = strings.emptySentFolderSubtitle;
            break;
        case 'Drafts':
            // titleText is default value "Nothing in Drafts"
            subtitleText = strings.emptyDraftsFolderSubtitle;
            break;
        case 'Deleted Items':
            // titleText is default value "Nothing in Deleted Items"
            subtitleText = strings.emptyJunkAndDeletedFolderSubtitle;
            break;
        default:
            break;
    }

    const imageSrc = isDarkTheme
        ? emptyScreenToDarkImageMap[folderName] || emptyScreenToDarkImageMap[DEFAULT]
        : emptyScreenToImageMap[folderName] || emptyScreenToImageMap[DEFAULT];

    return {
        title: { text: titleText },
        subtitle: { text: subtitleText },
        imageSrc,
    };
};
